/** Seletor de área no topo da barra lateral (24/10/2026). Só aparece para quem tem
 *  mais de uma área: com uma só, não há para onde trocar.
 *
 *  Lista só as áreas que a pessoa alcança, e "Todas as áreas" volta à tela de
 *  escolha. Trocar de área leva à primeira tela da área que ela alcança. */
import { useState } from 'react'
import Box from '@mui/material/Box'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import Divider from '@mui/material/Divider'
import Tooltip from '@mui/material/Tooltip'
import { alpha } from '@mui/material/styles'
import { Check, ChevronsUpDown, LayoutGrid } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../core/auth/useAuth'
import { useI18n } from '../../core/i18n/useI18n'
import { AREAS_PATH, areaPorChave, hubDaArea, type AreaKey } from '../../core/areas/areas'
import { AreaArte } from '../../core/areas/AreaArte'
import { areasDoHub, primeiraTelaDaArea } from '../../core/areas/acesso'
import { LEAF_RADIUS_SM } from '../../styles/theme'

export function AreaSwitcher({ atual, collapsed = false, onNavigate }: {
  atual: AreaKey
  collapsed?: boolean
  onNavigate?: () => void
}) {
  const { t } = useI18n()
  const { user } = useAuth()
  // Só as áreas do hub da área atual: a Farm, com uma área só, não mostra seletor.
  const areas = areasDoHub(user, hubDaArea(atual))
  const navigate = useNavigate()
  const [ancora, setAncora] = useState<HTMLElement | null>(null)

  if (areas.length < 2) return null
  const area = areaPorChave(atual)
  const nome = t(area.labelKey)

  function ir(destino: string) {
    setAncora(null)
    onNavigate?.()
    navigate(destino)
  }

  const botao = (
    <ListItemButton
      onClick={(e) => setAncora(e.currentTarget)}
      aria-haspopup="menu"
      aria-expanded={Boolean(ancora)}
      aria-label={t('areas.seletor.rotulo', { area: nome })}
      sx={{
        borderRadius: LEAF_RADIUS_SM,
        border: '1px solid',
        borderColor: 'divider',
        minHeight: 40,
        px: collapsed ? 0 : 1.25,
        justifyContent: collapsed ? 'center' : 'flex-start',
        '&.Mui-focusVisible': { boxShadow: (t) => `inset 0 0 0 2px ${alpha(t.palette.marca.main, 0.4)}` },
      }}
    >
      <ListItemIcon sx={{ minWidth: collapsed ? 0 : 30, color: 'primary.main', justifyContent: 'center' }}>
        <AreaArte nos={area.arte} size={18} />
      </ListItemIcon>
      {!collapsed && (
        <>
          <ListItemText
            primary={nome}
            primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: 600, noWrap: true }}
          />
          <Box component="span" sx={{ display: 'flex', color: 'text.secondary' }}>
            <ChevronsUpDown size={16} aria-hidden />
          </Box>
        </>
      )}
    </ListItemButton>
  )

  return (
    <Box sx={{ px: collapsed ? 1 : 1.5, pb: 1, flex: 'none' }}>
      {collapsed ? <Tooltip title={nome} placement="right" arrow>{botao}</Tooltip> : botao}
      <Menu
        anchorEl={ancora}
        open={Boolean(ancora)}
        onClose={() => setAncora(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{ paper: { sx: { minWidth: 220, mt: 0.5 } } }}
      >
        {areas.map((chave) => {
          const def = areaPorChave(chave)
          const destino = primeiraTelaDaArea(chave, user)
          const ehAtual = chave === atual
          return (
            <MenuItem
              key={chave}
              selected={ehAtual}
              disabled={!destino}
              onClick={() => destino && ir(destino)}
            >
              <ListItemIcon sx={{ color: 'primary.main' }}>
                <AreaArte nos={def.arte} size={18} />
              </ListItemIcon>
              <ListItemText primary={t(def.labelKey)} />
              {ehAtual && <Check size={16} aria-hidden />}
            </MenuItem>
          )
        })}
        <Divider />
        <MenuItem onClick={() => ir(AREAS_PATH)}>
          <ListItemIcon><LayoutGrid size={18} /></ListItemIcon>
          <ListItemText primary={t('areas.seletor.todas')} />
        </MenuItem>
      </Menu>
    </Box>
  )
}
